import {
  Inventory2Outlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Grid2,
  LinearProgress,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { observer } from "mobx-react";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  SwayyProduct,
  SwayyProductAction,
  SwayyProductStatus,
} from "../../../models/products/SwayyProduct";
import { SwayyProductsStore } from "../../../stores/SwayyProductsStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";
import InlineCopyButton from "../common/InlineCopyButton";
import SwayyProductActionMenu from "./SwayyProductActionMenu";
import { SwayyTableEmptyState } from "../SwayyPageShell";

const PAGE_SIZE_OPTIONS: FilterValue[] = [10, 30, 50, 100].map((size) => ({
  id: String(size),
  value: String(size),
}));

const formatUpdatedAt = (value: string) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));

const statusStyles: Record<
  SwayyProductStatus,
  { background: string; color: string }
> = {
  draft: {
    background: alpha(swayyColors.warning, 0.1),
    color: swayyColors.warning,
  },
  live: {
    background: alpha(swayyColors.success, 0.1),
    color: swayyColors.success,
  },
  archived: {
    background: swayyColors.pageBg,
    color: swayyColors.textSecondary,
  },
};

const Badge = ({
  label,
  background = swayyColors.pineLight,
  color = swayyColors.deepGreen,
}: {
  label: string;
  background?: string;
  color?: string;
}) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      minHeight: 24,
      px: 1.1,
      borderRadius: swayyRadii.full,
      backgroundColor: background,
      color,
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 1,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

const ProductIdentity = ({ product }: { product: SwayyProduct }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
    <Avatar
      src={product.thumbnail}
      alt=""
      variant="rounded"
      sx={{
        width: 48,
        height: 48,
        borderRadius: swayyRadii.md,
        border: `1px solid ${swayyColors.border}`,
        bgcolor: swayyColors.pineLight,
        color: swayyColors.deepGreen,
        flexShrink: 0,
      }}
    >
      {product.name.charAt(0)}
    </Avatar>
    <Box minWidth={0}>
      <CustomTypography
        variant="body1"
        fontWeight={700}
        color={swayyColors.deepGreen}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {product.name}
      </CustomTypography>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.35 }}>
        <CustomTypography
          variant="caption"
          color={swayyColors.textSecondary}
          sx={{ fontFamily: "monospace" }}
        >
          {product.sku}
        </CustomTypography>
        {product.sku ? (
          <InlineCopyButton
            value={product.sku}
            title="Copy SKU"
            ariaLabel="Copy product SKU"
          />
        ) : null}
      </Stack>
    </Box>
  </Stack>
);

const LoadingTable = () => (
  <TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
    <Table sx={{ minWidth: 880 }} aria-label="Loading products">
      <TableHead>
        <TableRow>
          {Array.from({ length: 8 }, (_, index) => (
            <TableCell key={index}>
              <Skeleton width={index === 0 ? 120 : 64} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: 7 }, (_, rowIndex) => (
          <TableRow key={rowIndex}>
            {Array.from({ length: 8 }, (_, cellIndex) => (
              <TableCell key={cellIndex}>
                <Skeleton
                  variant={cellIndex === 0 ? "rounded" : "text"}
                  width={cellIndex === 0 ? 190 : "75%"}
                  height={cellIndex === 0 ? 48 : 22}
                />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const LoadingCards = () => (
  <Stack spacing={1.5} sx={{ display: { xs: "flex", sm: "none" }, p: 2 }}>
    {Array.from({ length: 5 }, (_, index) => (
      <Paper
        key={index}
        variant="outlined"
        sx={{ p: 2, borderColor: swayyColors.border }}
      >
        <Stack direction="row" spacing={1.5}>
          <Skeleton variant="rounded" width={52} height={52} />
          <Box flex={1}>
            <Skeleton width="65%" />
            <Skeleton width="40%" />
          </Box>
        </Stack>
        <Skeleton sx={{ mt: 2 }} height={44} />
      </Paper>
    ))}
  </Stack>
);

interface ProductStateProps {
  onPrimaryAction: () => void;
}

const ProductState = ({ onPrimaryAction }: ProductStateProps) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    textAlign="center"
    sx={{ px: 2, py: { xs: 8, md: 10 }, minHeight: 360 }}
  >
    <Box
      sx={{
        width: 72,
        height: 72,
        display: "grid",
        placeItems: "center",
        borderRadius: swayyRadii.full,
        backgroundColor: swayyColors.pineLight,
        color: swayyColors.pine,
        mb: 2,
        "& .MuiSvgIcon-root": { fontSize: 34 },
      }}
    >
      <Inventory2Outlined />
    </Box>
    <CustomTypography
      variant="heading4"
      color={swayyColors.deepGreen}
      sx={{ mb: 1 }}
    >
      No matching products found
    </CustomTypography>
    <CustomTypography
      variant="body1"
      color={swayyColors.textSecondary}
      sx={{ maxWidth: 420, mb: 3 }}
    >
      Try changing or clearing your filters
    </CustomTypography>
    <AppButton
      label="Clear filters"
      onClick={onPrimaryAction}
      customStyles={{
        color: swayyColors.white,
        backgroundColor: swayyColors.pine,
        borderRadius: swayyRadii.md,
        "&:hover": { backgroundColor: swayyColors.pineHover },
      }}
    />
  </Stack>
);

const ProductsTable = ({
  products,
  onAction,
  onOpenProduct,
}: {
  products: SwayyProduct[];
  onAction: (product: SwayyProduct, action: SwayyProductAction) => void;
  onOpenProduct: (product: SwayyProduct) => void;
}) => (
  <TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
    <Table sx={{ minWidth: { sm: 820, lg: 1080 } }}>
      <TableHead>
        <TableRow>
          {[
            "Product",
            "Brand",
            "Status",
            "Sales Channel",
            "Variants",
            "Updated By",
            "Last Updated",
            "Actions",
          ].map((heading) => (
            <TableCell
              key={heading}
              align={heading === "Actions" ? "right" : "left"}
              sx={{
                display:
                  heading === "Brand"
                    ? { sm: "none", md: "table-cell" }
                    : heading === "Updated By"
                      ? { sm: "none", lg: "table-cell" }
                      : heading === "Last Updated"
                        ? { sm: "none", md: "table-cell" }
                        : "table-cell",
                py: 1.5,
                color: swayyColors.textSecondary,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                borderColor: swayyColors.border,
                backgroundColor: swayyColors.pageBg,
                whiteSpace: "nowrap",
              }}
            >
              {heading}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {products.map((product) => (
          <TableRow
            key={product.id}
            hover
            onClick={() => onOpenProduct(product)}
            sx={{
              cursor: "pointer",
              "&:last-child td": { borderBottom: 0 },
              "&:hover": { backgroundColor: swayyColors.pineLight },
            }}
          >
            <TableCell sx={{ minWidth: 240, borderColor: swayyColors.border }}>
              <ProductIdentity product={product} />
            </TableCell>
            <TableCell
              sx={{
                display: { sm: "none", md: "table-cell" },
                borderColor: swayyColors.border,
              }}
            >
              <CustomTypography variant="body2">
                {product.brandName}
              </CustomTypography>
            </TableCell>
            <TableCell sx={{ borderColor: swayyColors.border }}>
              <Badge
                label={
                  product.status.charAt(0).toUpperCase() +
                  product.status.slice(1)
                }
                {...statusStyles[product.status]}
              />
            </TableCell>
            <TableCell sx={{ borderColor: swayyColors.border }}>
              {product.channels.length > 0 ? (
                <Stack direction="row" spacing={0.5}>
                  {product.channels.map((channel) => (
                    <Badge key={channel} label={channel} />
                  ))}
                </Stack>
              ) : (
                <CustomTypography
                  variant="body2"
                  color={swayyColors.textSecondary}
                >
                  —
                </CustomTypography>
              )}
            </TableCell>
            <TableCell sx={{ borderColor: swayyColors.border }}>
              <CustomTypography variant="body2" fontWeight={700}>
                {product.variantCount}
              </CustomTypography>
            </TableCell>
            <TableCell
              sx={{
                display: { sm: "none", lg: "table-cell" },
                borderColor: swayyColors.border,
              }}
            >
              <CustomTypography variant="body2">
                {product.updatedBy}
              </CustomTypography>
            </TableCell>
            <TableCell
              sx={{
                display: { sm: "none", md: "table-cell" },
                borderColor: swayyColors.border,
                whiteSpace: "nowrap",
              }}
            >
              <CustomTypography
                variant="body2"
                color={swayyColors.textSecondary}
              >
                {formatUpdatedAt(product.updatedAt)}
              </CustomTypography>
            </TableCell>
            <TableCell
              align="right"
              sx={{ borderColor: swayyColors.border }}
              onClick={(event) => event.stopPropagation()}
            >
              <SwayyProductActionMenu
                product={product}
                onAction={onAction}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ProductsMobileCards = ({
  products,
  onAction,
  onOpenProduct,
}: {
  products: SwayyProduct[];
  onAction: (product: SwayyProduct, action: SwayyProductAction) => void;
  onOpenProduct: (product: SwayyProduct) => void;
}) => (
  <Stack spacing={1.5} sx={{ display: { xs: "flex", sm: "none" }, p: 1.5 }}>
    {products.map((product) => (
      <Paper
        key={product.id}
        variant="outlined"
        onClick={() => onOpenProduct(product)}
        sx={{
          p: 2,
          borderColor: swayyColors.border,
          borderRadius: swayyRadii.lg,
          cursor: "pointer",
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <ProductIdentity product={product} />
          <Box onClick={(event) => event.stopPropagation()}>
            <SwayyProductActionMenu product={product} onAction={onAction} />
          </Box>
        </Stack>
        <Grid2 container spacing={1.5} sx={{ mt: 1 }}>
          <Grid2 size={6}>
            <CustomTypography
              variant="caption"
              color={swayyColors.textSecondary}
            >
              Status
            </CustomTypography>
            <Box sx={{ mt: 0.5 }}>
              <Badge
                label={
                  product.status.charAt(0).toUpperCase() +
                  product.status.slice(1)
                }
                {...statusStyles[product.status]}
              />
            </Box>
          </Grid2>
          <Grid2 size={6}>
            <CustomTypography
              variant="caption"
              color={swayyColors.textSecondary}
            >
              Variants
            </CustomTypography>
            <CustomTypography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
              {product.variantCount}
            </CustomTypography>
          </Grid2>
          <Grid2 size={6}>
            <CustomTypography
              variant="caption"
              color={swayyColors.textSecondary}
            >
              Sales channel
            </CustomTypography>
            {product.channels.length > 0 ? (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                {product.channels.map((channel) => (
                  <Badge key={channel} label={channel} />
                ))}
              </Stack>
            ) : (
              <CustomTypography
                variant="body2"
                color={swayyColors.textSecondary}
                sx={{ mt: 0.5 }}
              >
                —
              </CustomTypography>
            )}
          </Grid2>
          <Grid2 size={6}>
            <CustomTypography
              variant="caption"
              color={swayyColors.textSecondary}
            >
              Last updated
            </CustomTypography>
            <CustomTypography variant="body2" sx={{ mt: 0.5 }}>
              {formatUpdatedAt(product.updatedAt)}
            </CustomTypography>
          </Grid2>
        </Grid2>
      </Paper>
    ))}
  </Stack>
);

const ProductsPagination = ({
  store,
  disabled,
}: {
  store: SwayyProductsStore;
  disabled: boolean;
}) => (
  <Grid2
    container
    spacing={2}
    alignItems="center"
    justifyContent="space-between"
    sx={{ px: { xs: 1.5, sm: 2 }, py: 2, borderTop: `1px solid ${swayyColors.border}` }}
  >
    <Grid2 size={{ xs: 12, md: "auto" }}>
      <CustomTypography variant="body2" color={swayyColors.textSecondary}>
        Showing {store.rangeStart}–{store.rangeEnd} of {store.totalResults}{" "}
        products
      </CustomTypography>
    </Grid2>
    <Grid2 size={{ xs: 12, md: "auto" }}>
      <Pagination
        count={store.totalPages}
        page={store.page}
        disabled={disabled}
        onChange={(_event, page) => store.setPage(page)}
        size="small"
        showFirstButton={false}
        showLastButton={false}
        siblingCount={0}
        sx={{
          display: "flex",
          justifyContent: { xs: "center", md: "flex-start" },
          "& .MuiPaginationItem-root.Mui-selected": {
            backgroundColor: swayyColors.pine,
            color: swayyColors.white,
          },
        }}
      />
    </Grid2>
    <Grid2 size={{ xs: 12, md: "auto" }}>
      <Box sx={{ width: { xs: "100%", md: 120 } }}>
        <AppSearchDropdownMulti
          title="Page size"
          isMultiSelect={false}
          local
          list={PAGE_SIZE_OPTIONS}
          value={PAGE_SIZE_OPTIONS.filter(
            (option) => option.id === String(store.pageSize),
          )}
          disabled={disabled}
          accentColor={swayyColors.pine}
          borderColor={swayyColors.border}
          borderRadius={swayyRadii.sm}
          inputHeight="38px"
          handleSelect={(items) => {
            const pageSize = Number(items[0]?.id);
            if (pageSize) store.setPageSize(pageSize);
          }}
        />
      </Box>
    </Grid2>
  </Grid2>
);

interface SwayyProductsListingProps {
  store: SwayyProductsStore;
  onClearFilters: () => void;
  onRetry: () => void;
  onAction: (product: SwayyProduct, action: SwayyProductAction) => void;
  onOpenProduct: (product: SwayyProduct) => void;
}

const SwayyProductsListing = ({
  store,
  onClearFilters,
  onRetry,
  onAction,
  onOpenProduct,
}: SwayyProductsListingProps) => {
  const isLoading =
    store.loadState === "idle" || store.loadState === "loading";
  const isError = store.loadState === "error";
  const isForbidden = store.loadState === "forbidden";
  const hasNoResults =
    !isLoading && !isError && !isForbidden && store.totalResults === 0;

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderColor: swayyColors.border,
        borderRadius: swayyRadii.lg,
        backgroundColor: swayyColors.white,
      }}
    >
      {store.isRefreshing && (
        <LinearProgress
          aria-label="Refreshing products"
          sx={{
            height: 2,
            backgroundColor: swayyColors.pineLight,
            "& .MuiLinearProgress-bar": {
              backgroundColor: swayyColors.pine,
            },
          }}
        />
      )}
      {isLoading ? (
        <>
          <LoadingTable />
          <LoadingCards />
        </>
      ) : isError ? (
        <SwayyTableEmptyState
          title="Couldn't load products"
          description={store.errorMessage || "Network Error"}
          actionLabel="Try again"
          onAction={onRetry}
          danger
        />
      ) : isForbidden ? (
        <SwayyTableEmptyState
          title="Couldn't load products"
          description="You don’t have permission to view products."
          actionLabel="Try again"
          onAction={onRetry}
          danger
        />
      ) : hasNoResults ? (
        <ProductState onPrimaryAction={onClearFilters} />
      ) : (
        <>
          <ProductsTable
            products={store.products}
            onAction={onAction}
            onOpenProduct={onOpenProduct}
          />
          <ProductsMobileCards
            products={store.products}
            onAction={onAction}
            onOpenProduct={onOpenProduct}
          />
        </>
      )}

      {!isError && !isForbidden && (
        <ProductsPagination
          store={store}
          disabled={isLoading || store.totalResults === 0}
        />
      )}
    </Paper>
  );
};

export default observer(SwayyProductsListing);
